import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as authActions from "../Authentication/redux/actions";
import { authHeaders, notificationInit, notificationRequest, readError } from "../NotificationList/api";

/*
 * CSAT / NPS reads.
 *
 * The same hop every other admin call takes - dashboard -> be-admin -> be-notification - and
 * the same three routes the survey page needs:
 *
 *   GET /surveys                    which surveys exist, and the questions in each
 *   GET /surveys/:code/summary      the score: average, histogram, and NPS where it applies
 *   GET /surveys/:code/responses    the answers behind the score, which is where comments live
 *
 * The definitions are served rather than hard-coded here so that a survey added in
 * be-notification's config appears in this page without a dashboard release.
 */

/*
 * The actions this screen asks for; notificationRequest decides how each one travels. The last
 * two read and set the switch for the automatic CSAT invitation.
 */
export const SURVEY_ACTIONS = {
  list: "surveys",
  summary: "surveySummary",
  responses: "surveyResponses",
  setting: "csatSetting",
  updateSetting: "updateCsatSetting"
};

export const RESPONSE_PAGE_SIZE = 20;

/** NPS reads promoters minus detractors; the passives in between are counted but not scored. */
export const NPS_BUCKET_LABEL = {
  PROMOTER: "Promoter",
  PASSIVE: "Passive",
  DETRACTOR: "Detractor"
};

/* Re-exported so the report screen and its test keep one name for it. */
export { buildQueryString } from "../NotificationList/api";

export class SurveyRequestError extends Error {
  constructor(status, detail) {
    super(detail || "Survey request failed");
    this.name = "SurveyRequestError";
    this.status = status;
    this.detail = detail;
  }
}

/**
 * A GET against the survey routes, carrying the operator's own session.
 *
 * There is no axios interceptor in this dashboard - every caller attaches the admin headers by
 * hand and deals with 401 itself - so this hook is that boilerplate in one place. A 401 signs
 * the operator out and resolves to `{ signedOut: true }`, so the caller stops quietly instead
 * of rendering an error nobody will be around to read.
 */
export function useSurveyRequest() {
  const accessToken = useSelector(state => state.auth.accessToken);
  const tGUID = useSelector(state => state.auth.GUID);
  const dispatch = useDispatch();

  return useCallback(
    async (action, args) => {
      const call = notificationRequest(action, args || {});
      const request = await fetch(call.url, notificationInit(call, authHeaders(accessToken, tGUID)));

      if (request.status === 401) {
        dispatch(authActions.autoSignOut());
        return { signedOut: true };
      }

      if (!request.ok) {
        throw new SurveyRequestError(request.status, await readError(request));
      }

      return { data: await request.json() };
    },
    [accessToken, tGUID, dispatch]
  );
}

/** "3.43" rather than "3.4299999999999997", and a dash where there is nothing to average. */
export const formatScore = (value, total) => {
  if (!total) {
    return "-";
  }
  const decimals = 2;
  return Number(value || 0).toFixed(decimals);
};

export const formatDateTime = value => {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  return isNaN(date.getTime()) ? "-" : date.toLocaleString("th-TH");
};

/** Today and the same day some months back, as the yyyy-mm-dd a date input wants. */
export const defaultRange = monthsBack => {
  const to = new Date();
  const from = new Date();
  from.setMonth(from.getMonth() - monthsBack);
  const iso = date => date.toISOString().slice(0, 10);
  return { from: iso(from), to: iso(to) };
};
